// @docs:
//   - docs/core/operations/ROUTING_SERVICE.md
//
// Admin › Routing — the boat: choose a polar file (any layout: the API recognises it) or a
// boat from the library, then see what was read: the range, the layout in words, what the
// API filled to complete the table to 0–180°, warnings in amber and errors in red with
// their row and column, the polar as a picture, and a compact table on request. Text
// files are sent as text; any other file (.xlsx, .xls, …) as its bytes in base64. A file
// that fails keeps the polar already loaded, and says so. The last polar loaded is
// remembered in this browser. Admin › Departure planner uses this same loader.

import { useState } from 'react';
import { api, type PolarLibraryEntry, type RoutingPolar, type RoutingPolarImport } from '../../services/api';
import { PolarPicture } from './PolarPicture';

const STORE = 'vortex.admin.routing.polar';

export function loadSavedPolar(): RoutingPolar | null {
  try {
    const raw = localStorage.getItem(STORE);
    const p = raw ? (JSON.parse(raw) as RoutingPolar) : null;
    return p && Array.isArray(p.twsKt) && Array.isArray(p.stw) ? p : null;
  } catch {
    return null;
  }
}

function savePolar(p: RoutingPolar | null) {
  try {
    if (p) localStorage.setItem(STORE, JSON.stringify(p));
    else localStorage.removeItem(STORE);
  } catch {
    // A convenience only: without storage the polar is loaded again next time.
  }
}

/** Polar files that are text; anything else is sent as bytes. */
const TEXT_FILE = /\.(csv|pol|txt|tsv|dat)$/i;
export const POLAR_ACCEPT = '.csv,.pol,.txt,.tsv,.dat,.xlsx,.xls';

/** The file as text, or as base64 of its bytes (FileReader: wider support than Blob). */
function readPolarFile(file: File): Promise<string | { base64: string }> {
  const asText = TEXT_FILE.test(file.name);
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      if (asText) return resolve(String(r.result ?? ''));
      const bytes = new Uint8Array(r.result as ArrayBuffer);
      let s = '';
      for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
      resolve({ base64: btoa(s) });
    };
    r.onerror = () => reject(r.error ?? new Error('Could not read the file'));
    if (asText) r.readAsText(file);
    else r.readAsArrayBuffer(file);
  });
}

/** "TWS 4–30 kn · TWA 0–180°" */
export function polarSummary(p: RoutingPolar): string {
  return `TWS ${p.twsKt[0]}–${p.twsKt[p.twsKt.length - 1]} kn · TWA ${p.twaDeg[0]}–${p.twaDeg[p.twaDeg.length - 1]}°`;
}

/** About eight angles for the table preview: the first sailed angle, then the usual points of sail. */
export function keyAngles(p: RoutingPolar): number[] {
  const first = p.twaDeg.find((_, i) => p.stw[i].some((v) => v > 0));
  const want = [...(first !== undefined && first < 45 ? [first] : []), 45, 60, 90, 110, 135, 150, 180];
  return want.filter((a) => p.twaDeg.includes(a));
}

type Issue = RoutingPolarImport['issues'][number];

/** "Line 6, column 4: …" — a workbook's places are rows. */
function issueText(i: Issue, format: string): string {
  if (!i.line) return i.message;
  return `${format === 'xlsx' ? 'Row' : 'Line'} ${i.line}${i.column ? `, column ${i.column}` : ''}: ${i.message}`;
}

function Issues({ issues, format }: { issues: Issue[]; format: string }) {
  if (!issues.length) return null;
  return (
    <ul className="text-[13px] space-y-1">
      {issues.slice(0, 12).map((i, k) => (
        <li key={k} className={i.level === 'error' ? 'text-red-300' : 'text-amber-300'}>{issueText(i, format)}</li>
      ))}
      {issues.length > 12 && <li className="text-slate-400">…and {issues.length - 12} more.</li>}
    </ul>
  );
}

const BUTTON = 'px-3 py-1.5 rounded-lg border border-slate-600 text-[14px] text-slate-200 hover:bg-slate-700 font-semibold';

export function RoutingBoat({ polar, onPolar }: { polar: RoutingPolar | null; onPolar: (p: RoutingPolar | null) => void }) {
  /** What was read for the polar shown. */
  const [result, setResult] = useState<RoutingPolarImport | null>(null);
  /** The last file that could not be used (the polar shown is unchanged). */
  const [failed, setFailed] = useState<RoutingPolarImport | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [library, setLibrary] = useState<PolarLibraryEntry[] | null>(null);
  const [showLibrary, setShowLibrary] = useState(false);
  const [showTable, setShowTable] = useState(false);

  async function apply(get: () => Promise<RoutingPolarImport>) {
    setBusy(true);
    setError(null);
    try {
      const r = await get();
      if (r.polar) {
        setResult(r);
        setFailed(null);
        onPolar(r.polar);
        savePolar(r.polar);
      } else setFailed(r);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const load = (file: File | undefined) => file && apply(async () => api.adminRoutingPolar(file.name, await readPolarFile(file)));

  async function openLibrary() {
    setShowLibrary((v) => !v);
    if (library) return;
    try {
      setLibrary(await api.polarLibrary());
    } catch (e) {
      setError((e as Error).message);
    }
  }

  const pick = (id: string) => {
    setShowLibrary(false);
    void apply(() => api.polarLibraryPolar(id));
  };

  const useDemo = () => {
    onPolar(null);
    savePolar(null);
    setResult(null);
    setFailed(null);
  };

  const shown = result?.polar === polar ? result : null;
  const angles = polar ? keyAngles(polar) : [];

  return (
    <div className="space-y-3">
      <span className="block text-[13px] font-semibold text-slate-300">Boat</span>
      <div className="flex items-center gap-3 flex-wrap">
        <label className={`${BUTTON} cursor-pointer ${busy ? 'opacity-60' : ''}`}>
          Choose a polar file
          <input type="file" accept={POLAR_ACCEPT} className="sr-only" disabled={busy} onChange={(e) => { void load(e.target.files?.[0]); e.target.value = ''; }} />
        </label>
        <button type="button" className={BUTTON} disabled={busy} onClick={() => void openLibrary()} aria-expanded={showLibrary}>
          Choose from the boat library
        </button>
        {polar && (
          <button type="button" className="px-3 py-1.5 rounded-lg text-[14px] text-slate-400 hover:text-slate-200" onClick={useDemo}>
            Use the demonstration polar
          </button>
        )}
      </div>
      {busy && <p className="text-[13px] text-slate-400">Reading…</p>}

      {showLibrary && (
        <div className="rounded-lg border border-slate-700 bg-slate-900/60 p-2 space-y-1">
          {!library && <p className="text-[13px] text-slate-400 px-2">Loading the boat library…</p>}
          {library?.length === 0 && <p className="text-[13px] text-slate-400 px-2">The boat library is empty.</p>}
          {library?.map((e) => (
            <button key={e.id} type="button" className="block w-full text-left px-2 py-1.5 rounded hover:bg-slate-800" onClick={() => pick(e.id)}>
              <span className="block text-[14px] text-slate-100 font-semibold">{e.name}</span>
              <span className="block text-[13px] text-slate-400">{e.source} · TWS {e.twsRange[0]}–{e.twsRange[1]} kn</span>
            </button>
          ))}
        </div>
      )}

      {error && <p className="text-[13px] text-red-300">{error}</p>}
      {failed && (
        <div className="rounded-lg border border-red-900/60 bg-red-950/30 p-3 space-y-1">
          <p className="text-[14px] text-red-200 font-semibold">{polar ? 'That file could not be read; your polar is unchanged.' : 'That file could not be read.'}</p>
          <Issues issues={failed.issues} format={failed.format} />
        </div>
      )}

      {polar ? (
        <section aria-label="Your polar" className="rounded-lg border border-slate-700 bg-slate-800/60 p-3 space-y-3">
          <div>
            <p className="text-[15px] text-slate-100 font-semibold">{polar.name}</p>
            <p className="text-[14px] text-slate-300">{polarSummary(polar)}</p>
            {shown?.readAs && <p className="text-[14px] text-slate-300">{shown.readAs}</p>}
          </div>
          {shown?.notes && shown.notes.length > 0 && (
            <ul aria-label="What was filled" className="list-disc pl-5 text-[13px] text-slate-300 space-y-0.5">
              {shown.notes.map((n) => <li key={n}>{n}</li>)}
            </ul>
          )}
          {shown && <Issues issues={shown.issues} format={shown.format} />}
          <PolarPicture polar={polar} />
          <div>
            <button type="button" className="text-[13px] text-sky-300 hover:text-sky-200 font-semibold" onClick={() => setShowTable((v) => !v)} aria-expanded={showTable}>
              {showTable ? 'Hide the table' : 'Show the table'}
            </button>
            {showTable && (
              <div className="overflow-x-auto mt-2">
                <table className="text-[13px] text-slate-200 tabular-nums">
                  <thead>
                    <tr>
                      <th scope="col" className="text-left pr-3 text-slate-400 font-semibold">TWA \ TWS</th>
                      {polar.twsKt.map((s) => <th key={s} scope="col" className="text-right px-2 text-slate-400 font-semibold">{s}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {angles.map((a) => {
                      const i = polar.twaDeg.indexOf(a);
                      return (
                        <tr key={a}>
                          <th scope="row" className="text-left pr-3 text-slate-400 font-semibold">{a}°</th>
                          {polar.stw[i].map((v, j) => <td key={j} className="text-right px-2">{v.toFixed(1)}</td>)}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="text-[13px] text-slate-400 mt-1">Boat speed in knots at key angles; the polar holds every 5°.</p>
              </div>
            )}
          </div>
        </section>
      ) : (
        <p className="text-[14px] text-slate-300">The demonstration 40 ft cruising polar (synthetic, not your boat)</p>
      )}
    </div>
  );
}
